import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import {
  DatastoreCollection,
  FreelancerReputation,
  LocalHourlyRate,
  PortfolioItem,
  ProfileCategory,
  ProfileViewUser,
  toNumber,
  TopSkill,
} from '@freelancer/datastore';
import { ModalService } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { HeadingSize } from '@freelancer/ui/heading';
import { IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { ModalSize } from '@freelancer/ui/modal';
import { FontColor, FontSize, FontWeight } from '@freelancer/ui/text';
import { ContentTypeApi } from 'api-typings/users/users';
import { HireMeWebappModalComponent } from 'app/hire-me/hire-me-webapp-modal/hire-me-webapp-modal.component';
import { HireMeUser } from 'app/hire-me/hire-me.component';
import {
  AwardProjectHireMeState,
  AwardProjectModel,
} from 'app/user-profile/user-profile-award-project.types';
import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
  template: `
    <fl-grid
      *ngIf="portfolioItem$ | async as portfolioItem; else loading"
      flTrackingSection="UserPortfolioModal"
      [vAlign]="VerticalAlignment.VERTICAL_STRETCH"
    >
      <fl-col
        class="ItemColumn"
        [ngClass]="{
          'ItemColumn--dark': darkBackground$ | async
        }"
        [col]="12"
        [colTablet]="8"
        [ngSwitch]="portfolioItem.contentType"
      >
        <app-user-portfolio-item-heading
          [categories]="itemCategories$ | async"
          [darkBackground]="darkBackground$ | async"
          [portfolioItem]="portfolioItem"
        >
        </app-user-portfolio-item-heading>
        <app-article-item
          *ngSwitchCase="'article'"
          [portfolioItem]="portfolioItem"
        ></app-article-item>
        <app-picture-item
          *ngSwitchCase="'picture'"
          [portfolioItem]="portfolioItem"
        ></app-picture-item>
        <app-video-item
          *ngSwitchCase="'video'"
          [portfolioItem]="portfolioItem"
        ></app-video-item>
        <app-audio-item
          *ngSwitchCase="'audio'"
          [portfolioItem]="portfolioItem"
        ></app-audio-item>
        <app-code-item
          *ngSwitchCase="'code'"
          [portfolioItem]="portfolioItem"
        ></app-code-item>
        <app-other-item
          *ngSwitchDefault
          [portfolioItem]="portfolioItem"
        ></app-other-item>
      </fl-col>
      <fl-col [colTablet]="4" [col]="12" class="SideUserColumn">
        <fl-bit class="SideUserDetails">
          <app-user-portfolio-user-details
            [flMarginBottom]="Margin.SMALL"
            [isOnline]="isOnline$ | async"
            [user]="user$ | async"
          >
          </app-user-portfolio-user-details>
          <app-user-portfolio-user-reputation
            [localRate]="localHourlyRate$ | async"
            [reputation]="reputation$ | async"
          >
          </app-user-portfolio-user-reputation>
          <app-user-profile-hire-me-action-state
            [awardProjectHireMeState$]="awardProjectHireMeState$"
          >
            <ng-container loading-state>
              <ng-template [ngTemplateOutlet]="loading"></ng-template>
            </ng-container>

            <ng-container award-project-state>
              <ng-container
                *ngIf="
                  awardProjectBid$ | async as awardProjectModel;
                  else loading
                "
              >
                <fl-button
                  display="block"
                  i18n="User Portfolio sidebar Award"
                  flTrackingLabel="PortfolioItemPreviewAwardMeButton"
                  [color]="ButtonColor.SUCCESS"
                  [flMarginBottom]="Margin.MID"
                  [fragment]="'proposals'"
                  [link]="awardProjectModel.projectLink"
                  [queryParams]="{ awardBid: awardProjectModel.bidId }"
                  [size]="ButtonSize.LARGE"
                >
                  Award
                </fl-button>
              </ng-container>
            </ng-container>

            <ng-container hire-me-state>
              <fl-button
                *ngIf="hireMeUser$ | async as hireMeUser"
                display="block"
                i18n="User Portfolio sidebar Hire Me"
                flTrackingLabel="PortfolioItemPreviewHireMeButton"
                [flMarginBottom]="Margin.MID"
                [color]="ButtonColor.SECONDARY"
                [size]="ButtonSize.LARGE"
                (click)="hiremeAction()"
              >
                Hire {{ hireMeUser.profileUserDisplayName }}
              </fl-button>
            </ng-container>

            <ng-template #loading>
              <fl-button
                display="block"
                flTrackingLabel="PortfolioItemPreviewLoadingButton"
                [flMarginBottom]="Margin.MID"
                [size]="ButtonSize.LARGE"
                [disabled]="true"
                [busy]="true"
              ></fl-button>
            </ng-template>
          </app-user-profile-hire-me-action-state>

          <fl-bit [flMarginBottom]="Margin.MID">
            <fl-text
              i18n="About title label"
              [size]="FontSize.MID"
              [color]="FontColor.DARK"
              [weight]="FontWeight.BOLD"
              [flMarginBottom]="Margin.XSMALL"
            >
              About the project
            </fl-text>
            <fl-text
              class="SideUserDetails-projectDesc"
              [displayLineBreaks]="true"
              [size]="FontSize.MID"
            >
              <fl-truncated-text
                [text]="portfolioItem.description"
                [limit]="250"
              ></fl-truncated-text>
            </fl-text>
          </fl-bit>
          <fl-bit
            *ngIf="itemSkills$ | async as itemSkills"
            [flMarginBottom]="Margin.MID"
          >
            <fl-text
              *ngIf="0 < itemSkills.length"
              class="SideUserDetails-skills-title"
              i18n="Skill title label"
              [size]="FontSize.SMALL"
              [color]="FontColor.MID"
              [flMarginBottom]="Margin.XSMALL"
            >
              Skills
            </fl-text>
            <fl-bit class="SideUserDetails-skills-item">
              <fl-button
                *ngFor="let skill of itemSkills"
                flTrackingLabel="SkillNavigate"
                i18n="Skill name label"
                [color]="ButtonColor.TRANSPARENT_DARK"
                [flMarginBottom]="Margin.XXXSMALL"
                [flMarginRight]="Margin.XXXSMALL"
                [link]="'/freelancers/skills/' + skill.seoUrl"
                [size]="ButtonSize.MINI"
              >
                {{ skill.name }}
              </fl-button>
            </fl-bit>
          </fl-bit>
          <fl-hr
            [flMarginBottom]="
              !(isViewerProfileOwner$ | async) ? Margin.MID : Margin.NONE
            "
          ></fl-hr>
          <app-user-portfolio-share-item
            *ngIf="!(isViewerProfileOwner$ | async)"
            [user]="user$ | async"
            [portfolioItem]="portfolioItem$ | async"
          >
          </app-user-portfolio-share-item>
        </fl-bit>
      </fl-col>
    </fl-grid>
    <ng-template #loading>
      <fl-grid>
        <fl-col [col]="8">
          <fl-loading-text [rows]="1" [padded]="false"></fl-loading-text>
        </fl-col>
        <fl-col [col]="4">
          <fl-loading-text
            *ngFor="let n of [0, 1, 2, 3]"
            [rows]="1"
            [padded]="false"
          ></fl-loading-text>
        </fl-col>
      </fl-grid>
    </ng-template>
  `,
  styleUrls: ['./user-portfolio-item-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPortfolioItemOverlayComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  IconSize = IconSize;
  Margin = Margin;
  VerticalAlignment = VerticalAlignment;

  @Input() awardProjectBid$?: Rx.Observable<AwardProjectModel>;
  @Input() awardProjectHireMeState$?: Rx.Observable<AwardProjectHireMeState>;
  @Input() categoriesCollection: DatastoreCollection<'profileCategories'>;
  @Input() hireMeUser$: Rx.Observable<HireMeUser>;
  @Input() isOnline$: Rx.Observable<boolean>;
  @Input() localHourlyRate$: Rx.Observable<LocalHourlyRate | undefined>;
  @Input() portfolioItem$: Rx.Observable<PortfolioItem>;
  @Input() reputation$: Rx.Observable<FreelancerReputation>;
  @Input() skillsCollection: DatastoreCollection<'topSkills'>;
  @Input() user$: Rx.Observable<ProfileViewUser>;

  isViewerProfileOwner$: Rx.Observable<boolean>;
  itemCategories$: Rx.Observable<ReadonlyArray<ProfileCategory>>;
  itemSkills$: Rx.Observable<ReadonlyArray<TopSkill>>;
  darkBackground$: Rx.Observable<boolean>;

  constructor(private auth: Auth, private modalService: ModalService) {}

  ngOnInit() {
    this.itemSkills$ = Rx.combineLatest([
      this.skillsCollection.valueChanges(),
      this.portfolioItem$,
    ]).pipe(
      map(([skills, item]) => skills.filter(s => item.jobs.includes(s.id))),
    );

    this.itemCategories$ = Rx.combineLatest([
      this.categoriesCollection.valueChanges(),
      this.portfolioItem$,
    ]).pipe(
      map(([categories, item]) =>
        categories.filter(c => item.categories.includes(c.id)),
      ),
    );

    this.darkBackground$ = this.portfolioItem$.pipe(
      map(
        item =>
          item.contentType === ContentTypeApi.AUDIO ||
          item.contentType === ContentTypeApi.VIDEO,
      ),
    );

    this.isViewerProfileOwner$ = Rx.combineLatest([
      this.user$.pipe(map(user => user.id)),
      this.auth.getUserId().pipe(map(toNumber)),
    ]).pipe(map(([profileUserId, userId]) => profileUserId === userId));
  }

  hiremeAction() {
    this.modalService.close();
    this.hireMeUser$
      .pipe(take(1))
      .toPromise()
      .then(hireMeUser => {
        const userId = hireMeUser.profileUserId;
        this.modalService.open(HireMeWebappModalComponent, {
          inputs: {
            profileUserId: userId,
          },
          size: ModalSize.XSMALL,
          edgeToEdge: true,
        });
      });
  }
}
